import { useTranslation } from "react-i18next";
import { GridColumn, GridRow, Icon, Popup } from "semantic-ui-react";

import { Editable } from "components";
import { useShowPriceInVat } from "hooks";
import { COMMUNICATION_EVENTS, COMMUNICATION_RESULT_STATUSES } from "models";
import { CustomerCommunicationAnswer } from "modules/AppointmentDetails/components/Interventions/components";
import { TyreActions } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/TyreReplacementTableRow/components";
import "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/TyreReplacementTableRow/TyreReplacementTableRow.scss";
import { getFullTyreDescription, getTyreSize } from "modules/AppointmentDetails/components/VehicleTyres/utils";
import { getDisplayedPrice, getTyreSeasonIcon } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { Appointment, DealerLocation, TYRETEAM_CHANNELS, VehicleTyre, VehicleTyreReplacement } from "models";
import type { DeliveryPopup } from "modules/AppointmentDetails/components/VehicleTyres/hooks";

type TyreTableItemProps = {
  vehicleTyre: VehicleTyre;
  replacement: VehicleTyreReplacement;
  appointment: Appointment;
  location: DealerLocation | undefined;
  onUpdateReplacement: (replacement: VehicleTyreReplacement) => void;
  onOrderReplacement: (replacement: VehicleTyreReplacement) => void;
  getDeliveryChannelName: (channel?: TYRETEAM_CHANNELS | null) => string;
  getDeliveryPopup: (delivery?: string | null) => DeliveryPopup | null;
  canUpdateAppointment: boolean;
  isTyreTeamEnabled: boolean;
  isTyreNetEnabled: boolean;
  tyreOrderingEnabled: boolean;
  readOnly?: boolean;
};

const DELIVERY_TYPES = {
  PICKUP: "PICKUP",
  SAMEDAY: "SAMEDAY",
  OVERNIGHT: "OVERNIGHT"
};

const getDeliveryColor = (replacement: VehicleTyreReplacement) => {
  if (replacement.order_placed) return "green";

  if (!replacement.delivery_method) return;
  if (replacement.delivery_method.startsWith(DELIVERY_TYPES.PICKUP)) return "orange";
  if (replacement.delivery_method.startsWith(DELIVERY_TYPES.SAMEDAY)) return "green";
  if (replacement.delivery_method.startsWith(DELIVERY_TYPES.OVERNIGHT)) return "#2185d0";
};

const getCommunicationResultStatus = (status: COMMUNICATION_RESULT_STATUSES | undefined, selected: boolean) => {
  if (!status || ([COMMUNICATION_RESULT_STATUSES.OkStatus, COMMUNICATION_RESULT_STATUSES.ContactMeStatus].includes(status) && !selected)) return;

  return status;
};

export const TyreReplacementTableRow = ({
  vehicleTyre,
  replacement,
  appointment,
  location,
  onUpdateReplacement,
  onOrderReplacement,
  getDeliveryPopup,
  getDeliveryChannelName,
  canUpdateAppointment,
  isTyreTeamEnabled,
  isTyreNetEnabled,
  tyreOrderingEnabled,
  readOnly
}: TyreTableItemProps) => {
  const { t } = useTranslation();

  const showPriceInVat = useShowPriceInVat();

  const displayedPrice = getDisplayedPrice(replacement.price, location, showPriceInVat);
  const deliveryInfo = getDeliveryPopup(replacement.delivery_method);
  const diagnoseOverviewStatus = appointment.customer_communication?.events
    ?.find(e => e.type === COMMUNICATION_EVENTS.DiagnoseOverviewOpened)
    ?.replacement_results?.find(r => r.vehicle_tyre_replacement_id === replacement.id)?.communication_result_status;
  const commResultStatus = getCommunicationResultStatus(diagnoseOverviewStatus, replacement.selected);
  const tyreDescription = getFullTyreDescription(replacement.tyre, isTyreNetEnabled);
  const showOrderButton =
    (isTyreTeamEnabled && replacement.tyre_team_system_number && (replacement.order_placed || canUpdateAppointment)) ||
    (isTyreNetEnabled && (replacement.order_placed || canUpdateAppointment));

  return (
    <GridRow>
      <GridColumn width={2} className="TyreReplacementTableItem">
        <p className="replace-with">
          <strong>{t("v8_replace_with", "Replace with")}:</strong>
        </p>
      </GridColumn>
      <GridColumn width={2}>
        <Popup
          hoverable
          content={getTyreSize(replacement.tyre)}
          trigger={
            <div className="tyre-season-container">
              <Icon className={getTyreSeasonIcon(replacement.tyre?.season)} />
              <p>{getTyreSize(replacement.tyre)}</p>
            </div>
          }
        />
      </GridColumn>
      <GridColumn width={4}>
        <Popup hoverable content={tyreDescription} trigger={<p>{tyreDescription}</p>} />
      </GridColumn>
      <GridColumn width={1}>
        <Popup hoverable content={replacement.tyre?.manufacturer} trigger={<p>{replacement.tyre?.manufacturer}</p>} />
      </GridColumn>
      <GridColumn width={1}></GridColumn>
      <GridColumn width={1}>€ {formatPrice(displayedPrice)}</GridColumn>
      <GridColumn width={3}>
        <Editable hoverable value={replacement.remark} onSubmit={remark => onUpdateReplacement({ ...replacement, remark })} disabled={!canUpdateAppointment} />
      </GridColumn>
      <GridColumn width={2}>
        <div className="TyreReplacementTableItem__actions">
          <CustomerCommunicationAnswer communicationResultStatus={commResultStatus} />
          <div className="status-container">
            <div
              className="action-icon-container"
              onClick={() => canUpdateAppointment && onUpdateReplacement({ ...replacement, visible_to_customer: !replacement.visible_to_customer })}
            >
              <Icon color={replacement.visible_to_customer ? "green" : "red"} className={`eye pointer small ${replacement.visible_to_customer ? "" : "slash"}`} />
            </div>

            {showOrderButton && (
              <Popup
                flowing
                position="top left"
                trigger={
                  <div
                    className="action-icon-container"
                    style={replacement.order_placed ? { color: "#FFF", backgroundColor: getDeliveryColor(replacement) } : {}}
                    onClick={() => canUpdateAppointment && onOrderReplacement(replacement)}
                  >
                    <Icon className={`shopping cart small ${tyreOrderingEnabled ? "pointer" : ""}`} />
                  </div>
                }
                content={
                  <div>
                    {deliveryInfo?.content ?? replacement.delivery_method}
                    <br />
                    {t("v8_ordering_channel", "Ordering Channel")}: {getDeliveryChannelName(replacement.tyre_team_channel)}
                  </div>
                }
              />
            )}

            <div className="action-icon-container">
              <TyreActions vehicleTyre={vehicleTyre} replacement={replacement} appointment={appointment} readOnly={readOnly || !canUpdateAppointment} />
            </div>
          </div>
        </div>
      </GridColumn>
    </GridRow>
  );
};
